import { usePostHog } from '@posthog/react';
import CancelButton from 'components/buttons/CancelButton';
import DeleteButton from 'components/buttons/DeleteButton';
import SaveButton from 'components/buttons/SaveButton';
import { useCreateDialogForm, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { useCreateDraft } from 'components/create-dialog/useCreateDraft';
import { DraftSaveMode } from 'components/drafts/DraftSaveMode';
import ActionHeader from 'components/forms/ActionHeader';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import ConfirmNavigation from 'components/navigation/ConfirmNavigation';
import Section from 'components/section-menu/Section';
import { HighlightedFieldsProvider, SectionActionsProvider } from 'components/section-menu/SectionActionsContext';
import SectionLayout from 'components/section-menu/SectionLayout';
import useFocusedEditMode from 'hooks/useFocusedEditMode';
import { cloneElement, useRef } from 'react';
import { useSelector } from 'store';
import { useAddPartyGroupMutation, useUpdatePartyGroupMutation } from 'store/api/groups';
import { ClassifierData } from 'types/classifiers';
import { PartyGroupForm } from 'types/groups';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useGoBack, useLeaveForm, useReturnToLocation } from 'utils/useNavigateWithReturnState';
import {
    GroupClassificationFields,
    GroupNameField,
    GroupTypeField,
    MembersFields,
    NotesField,
} from './form-sections/GroupFields';
import { useGroupForm } from './form-sections/useGroupForm';
import { groupFormToRequest } from './groupFormMapping';
import { useDeleteGroup } from './useDeleteGroup';

type Props = {
    type: 'add' | 'edit';
    initialData?: PartyGroupForm;
    classifiers: Record<string, ClassifierData>;
    refetch?: () => void;
    fallbackUrl?: string;
};

const GroupPageForm = ({ type = 'add', initialData, classifiers, refetch, fallbackUrl }: Props): JSX.Element => {
    useFocusedEditMode('groups');
    const { id: groupId } = useCreateRoute().params;
    const form = useGroupForm({ type, initialData, classifiers });
    const { methods, groupName } = form;
    useCreateDialogForm(methods);
    const draft = useCreateDraft({ record: 'group', methods, toPayload: groupFormToRequest });
    const { handleSubmit, control, reset } = methods;

    const { buttonsDisabled } = useSelector((state) => state.buttons);
    const returnTo = useReturnToLocation(fallbackUrl ? fallbackUrl : groupId ? `/group/${groupId}` : '/groups');
    const goBack = useGoBack(returnTo);
    const leaveForm = useLeaveForm();

    const posthog = usePostHog();
    const [addGroup] = useAddPartyGroupMutation();
    const [updateGroup] = useUpdatePartyGroupMutation();

    const bypassNavigationBlockerRef = useRef(false);

    const onSubmit = async (formData: PartyGroupForm, action: 'create' | 'update'): Promise<void> => {
        try {
            const data = groupFormToRequest(formData);

            if (action === 'create') {
                // A draft is created by completing it: the group keeps the draft's id.
                await (draft.draftId === null ? addGroup(data).unwrap() : draft.complete(data))
                    .then((resp) => {
                        posthog.capture('group_created', { group_type: data.groupTypeCode });
                        bypassNavigationBlockerRef.current = true;
                        reset({}, { keepValues: true });
                        leaveForm(fallbackUrl ? fallbackUrl : `/group/${resp.id}`);
                    })
                    .catch(console.error);
            } else if (action === 'update' && groupId && !isNaN(Number(groupId))) {
                await updateGroup({
                    ...data,
                    id: groupId,
                })
                    .unwrap()
                    .then(() => {
                        bypassNavigationBlockerRef.current = true;
                        if (refetch) refetch();
                        reset({}, { keepValues: true });
                        leaveForm(returnTo);
                    })
                    .catch(console.error);
            }
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const submitCreateGroup = () => {
        handleSubmit((data) => onSubmit(data, 'create'))();
    };

    const submitUpdateGroup = () => {
        handleSubmit((data) => onSubmit(data, 'update'))();
    };

    const handleDeleteGroup = useDeleteGroup(groupId, () => {
        bypassNavigationBlockerRef.current = true;
        reset({}, { keepValues: true });
    });

    const getFormTitle = () => {
        switch (type) {
            case 'edit':
                return groupName || t('groups.group');
            default:
                return t('groups.add-new-group');
        }
    };

    const getFormHeader = () => {
        const actions: JSX.Element[] = [];

        if (type === 'add') {
            actions.push(
                <CancelButton onClick={goBack} />,
                ...(draft.button ? [draft.button] : []),
                <SaveButton
                    permissions={['CREATE.PARTY_GROUPS']}
                    onClick={submitCreateGroup}
                    disabled={buttonsDisabled}
                />
            );
        } else if (type === 'edit') {
            actions.push(
                <CancelButton onClick={goBack} />,
                <DeleteButton
                    permissions={['DELETE.PARTY_GROUPS.ALL']}
                    onClick={handleDeleteGroup}
                    disabled={buttonsDisabled}
                />,
                <SaveButton
                    permissions={['UPDATE.PARTY_GROUPS.ALL', 'UPDATE.PARTY_GROUPS.MY_OWN']}
                    onClick={submitUpdateGroup}
                    disabled={buttonsDisabled}
                />
            );
        }

        return (
            <ActionHeader
                title={getFormTitle()}
                actions={actions.map((button, index) => cloneElement(button, { key: `action#${index}` }))}
                onBack={goBack}
                draft={draft.detail}
            />
        );
    };

    return (
        <SectionActionsProvider>
            <HighlightedFieldsProvider>
                <FormWrapper>
                    {getFormHeader()}
                    <DraftSaveMode mode={draft.mode}>
                        <SectionLayout>
                            <Section title="groups.choose-type" collapsible>
                                <GroupTypeField form={form} />
                            </Section>
                            <Section title="groups.group" collapsible>
                                <GroupNameField form={form} />
                                <MembersFields form={form} />
                                <GroupClassificationFields form={form} />
                            </Section>
                            <Section title="groups.notes" collapsible>
                                <NotesField form={form} />
                            </Section>
                        </SectionLayout>
                    </DraftSaveMode>
                    <ConfirmNavigation
                        control={control}
                        bypassRef={bypassNavigationBlockerRef}
                        draft={type === 'add' ? draft.leave : null}
                    />
                </FormWrapper>
            </HighlightedFieldsProvider>
        </SectionActionsProvider>
    );
};

export default GroupPageForm;
